/**
 * Error Fallback UI Component
 * Displays user-friendly error message with recovery options
 */

import { useState } from 'react'
import {
  MessageBar,
  MessageBarBody,
  Button,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import type { ErrorFallbackProps } from '../types/errors'
import { formatErrorInfo } from '../utils/errorLogger'

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: tokens.spacingVerticalXXXL,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  errorCard: {
    maxWidth: '600px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  messageBar: {
    width: '100%',
  },
  heading: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    marginBottom: tokens.spacingVerticalS,
  },
  message: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
    marginBottom: tokens.spacingVerticalM,
  },
  buttonGroup: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  detailsSection: {
    marginTop: tokens.spacingVerticalM,
  },
  detailsButton: {
    marginBottom: tokens.spacingVerticalS,
  },
  detailsContent: {
    backgroundColor: tokens.colorNeutralBackground2,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxHeight: '300px',
    overflowY: 'auto',
  },
})

export function ErrorFallback({
  error,
  errorInfo,
  onReset,
  onReload,
}: ErrorFallbackProps) {
  const styles = useStyles()
  const isDevelopment = import.meta.env.DEV
  const [showDetails, setShowDetails] = useState(false)

  return (
    <div className={styles.container}>
      <div className={styles.errorCard}>
        <MessageBar intent="error" className={styles.messageBar}>
          <MessageBarBody>
            <Text className={styles.heading}>Something went wrong</Text>
            <Text className={styles.message}>
              We're sorry, but something unexpected happened. You can try again or reload the page.
            </Text>
            <div className={styles.buttonGroup}>
              <Button appearance="primary" onClick={onReset}>
                Try Again
              </Button>
              <Button appearance="secondary" onClick={onReload}>
                Reload Page
              </Button>
            </div>
          </MessageBarBody>
        </MessageBar>

        {isDevelopment && (
          <div className={styles.detailsSection}>
            <Button
              appearance="subtle"
              onClick={() => setShowDetails(!showDetails)}
              className={styles.detailsButton}
            >
              {showDetails ? 'Hide' : 'Show'} Error Details
            </Button>
            {showDetails && (
              <div className={styles.detailsContent}>
                {formatErrorInfo(error, errorInfo)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
